import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

/**
 * Service for managing key-value data storage within the application.
 * Provides methods for initializing storage, manipulating stored data,
 * and retrieving information.
 *
 * This service ensures proper handling of storage readiness and provides
 * observables for asynchronous operations.
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {

  /**
   * Represents the storage mechanism used for persisting data.
   *
   * This variable can store an instance of the Storage interface or be null
   * if no storage mechanism is currently available or assigned.
   *
   * The Storage interface typically provides methods for accessing and managing
   * stored key-value pairs (e.g., localStorage or sessionStorage in web environments).
   */
  private _storage: Storage | null = null;
  /**
   * A BehaviorSubject that holds the readiness state of a storage system.
   * Emits a boolean value indicating whether the storage system is ready for operations.
   * Initially set to `false`, and can be updated to `true` once the storage is ready.
   */
  private storageReady$ = new BehaviorSubject<boolean>(false);

  /**
   * Creates an instance of the class.
   *
   * @param {Storage} storage - The storage instance to be used for managing data.
   * @return {void}
   */
  constructor(private storage: Storage) {}

  /**
   * Initializes the application storage system based on the user's login status.
   * If the user is logged in, the storage is created, configured, and populated with initial data.
   * If the user is not logged in, the initialization process is skipped.
   *
   * @return {Observable<void>} An observable that completes when the initialization process finishes.
   */
  init(): Observable<void> {
    return this.isUserLoggedIn().pipe(
      switchMap((isLoggedIn: boolean) => {
        if (isLoggedIn) {
          // If user is logged in, initialize storage and set it up
          return from(this.storage.create()).pipe(
            switchMap((storage: Storage) => this.setupStorage(storage)),
            switchMap(() => this.initStorageData()),
            catchError(err => {
              console.error('Error during storage setup:', err);
              return of(undefined); // Continue the stream despite the error
            })
          );
        } else {
          // If user is not logged in, log and return undefined
          return of(undefined);
        }
      })
    );
  }

  /**
   * Checks if a user is currently logged in by verifying the presence of a 'user' entry in local storage.
   *
   * @return {Observable<boolean>} An observable that emits `true` if a 'user' entry exists in local storage, and `false` otherwise.
   */
  private isUserLoggedIn(): Observable<boolean> {
    return of(!!localStorage.getItem('user'));
  }

  /**
   * Configures and initializes the provided storage.
   *
   * @param {Storage} storage - The storage instance to be set up and used within the application.
   * @return {Observable<void>} An observable that emits once the storage has been successfully set up.
   */
  private setupStorage(storage: Storage): Observable<void> {
    this._storage = storage;
    this.storageReady$.next(true);
    return of(undefined);
  }

  /**
   * Initializes storage data by retrieving storage keys and executing associated tasks.
   *
   * This method performs the following actions:
   * - Retrieves a list of storage keys.
   * - Executes any required tasks in parallel for these keys.
   * - Handles possible errors during task execution and ensures the observable completes gracefully.
   *
   * @return An observable that emits `undefined` when storage data initialization completes or errors are handled.
   */
  private initStorageData(): Observable<void> {
    return this.getStorageKeys().pipe(
      switchMap((keys: string[]) => {
        const tasks: Observable<any>[] = [];

        // If no tasks need to run, return undefined immediately
        if (tasks.length === 0) {
          return of(undefined);
        }

        // Run the tasks in parallel with forkJoin and map the result to undefined
        return forkJoin(tasks).pipe(
          map(() => undefined), // We don't care about the result, just map to undefined
          catchError(err => {
            console.error('Error initializing storage data:', err);
            return of(undefined); // Continue despite errors
          })
        );
      })
    );
  }

  /**
   * Retrieves all storage keys as an observable array of strings.
   *
   * @return {Observable<string[]>} An observable emitting an array of storage keys.
   */
  private getStorageKeys(): Observable<string[]> {
    return from(this._storage?.keys() ?? Promise.resolve([]));
  }

  /**
   * Stores a value in the storage with the given key.
   *
   * @param {string} key - The key under which the value will be stored.
   * @param {T} value - The value to store in the storage.
   * @return {void}
   */
  set<T>(key: string, value: T): void {
    this._storage?.set(key, value);
  }

  /**
   * Retrieves the value associated with a key from the storage.
   *
   * @param key The key for which to retrieve the value.
   * @returns An observable that emits the stored value or null if not found.
   */
  get<T>(key: string): Observable<T | null> {
    return this.storageReady$.pipe(
      filter((ready: boolean) => ready),
      switchMap(() => from(this._storage?.get(key) ?? Promise.resolve(null)))
    );
  }

  /**
   * Clears all stored data in the storage mechanism.
   *
   * @return {Observable<void>} An observable that completes once the storage is cleared.
   */
  clear(): Observable<void> {
    return from(this._storage?.clear() ?? Promise.resolve(undefined));
  }
}
